/**
 * Multi-tab leader election via BroadcastChannel.
 * Lowest tabId wins for a given chapter key; only the leader drains the outbox.
 */

export type TabLeaderOptions = {
  /** Unique per chapter editor instance scope, e.g. `${userId}::${bookId}::${chapterId}`. */
  scopeKey: string;
  channelName?: string;
  heartbeatMs?: number;
};

function newTabId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export class ChapterSyncTabLeader {
  readonly tabId: string;
  private readonly scopeKey: string;
  private readonly channel: BroadcastChannel | null;
  private readonly heartbeatMs: number;
  private leader = true;
  private timer: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  constructor(options: TabLeaderOptions) {
    this.tabId = newTabId();
    this.scopeKey = options.scopeKey;
    this.heartbeatMs = options.heartbeatMs ?? 2500;
    if (typeof BroadcastChannel === 'undefined') {
      this.channel = null;
      return;
    }
    this.channel = new BroadcastChannel(options.channelName ?? 'jw-chapter-sync-v1');
    this.channel.onmessage = (ev: MessageEvent) => {
      const data = ev.data as { type?: string; scopeKey?: string; tabId?: string } | null;
      if (!data || data.scopeKey !== this.scopeKey || typeof data.tabId !== 'string') return;
      if (data.type === 'claim' || data.type === 'heartbeat') {
        if (data.tabId < this.tabId) {
          this.leader = false;
        } else if (data.tabId > this.tabId) {
          this.leader = true;
          this.announce('claim');
        }
      }
    };
    this.announce('claim');
    this.timer = setInterval(() => {
      if (this.destroyed) return;
      if (this.leader) this.announce('heartbeat');
      else this.announce('claim');
    }, this.heartbeatMs);
  }

  isLeader(): boolean {
    return this.leader;
  }

  destroy(): void {
    this.destroyed = true;
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    try {
      this.channel?.close();
    } catch {
      // ignore
    }
  }

  private announce(type: 'claim' | 'heartbeat'): void {
    try {
      this.channel?.postMessage({ type, scopeKey: this.scopeKey, tabId: this.tabId });
    } catch {
      // ignore
    }
  }
}
